import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { getOwnerId } from 'legacy/lut/selectors/app'
import { uploadAttachments } from 'legacy/lut/services/files/uploadAttachments'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { AttachFile } from 'clickNgo/components/attachFile/AttachFile'
import { ACCEPTABLE_FILE_TYPES } from 'clickNgo/entities/partner/configs/files'
import { IAttachment } from 'clickNgo/models/file'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { FormCheckBox, useGetFormDataField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { LoaderOverlay } from 'core-components/library/loader'

import { PartnerDataFormKeys, PartnerDataFormSettings } from '../../../configs'
import { IPartnerDataFormData } from '../../../models'
import { ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS } from '../../../translations'

interface IUseMandatoryAttachmentsFormParams {
  onConfirm?: () => void
  onReject?: () => void
}

enum MandatoryAttachmentsFields {
  HasMandatoryAttachments = 'hasMandatoryAttachments',
  Attachments = 'attachments',
}

export const useMandatoryAttachmentsForm = ({
  onConfirm,
  onReject,
}: IUseMandatoryAttachmentsFormParams) => {
  const ownerId = useSelector(getOwnerId)
  const [isLoading, setIsLoading] = useState(false)
  const [notUploadedAttachments, setNotUploadedAttachments] = useState<string[]>([])
  const [actualData, updateActualData] = useGetFormDataField<IPartnerDataFormData>(
    PartnerDataFormKeys.Setting,
    PartnerDataFormSettings.ActualData
  )
  const [isMandatoryAttachmentsDialogVisible, setIsMandatoryAttachmentsDialogVisible] =
    useState(false)
  const [mandatoryAttachments, updateMandatoryAttachments] = useGetFormDataField<IAttachment[]>(
    PartnerDataFormKeys.MandatoryAttachments,
    MandatoryAttachmentsFields.Attachments
  )
  const [hasMandatoryAttachments, setHasMandatoryAttachments] = useGetFormDataField<boolean>(
    PartnerDataFormKeys.MandatoryAttachments,
    MandatoryAttachmentsFields.HasMandatoryAttachments
  )

  const renderNonUploadedAttachmentsMessage = useMemo(
    () => <span>{`Some files could not be uploaded: ${notUploadedAttachments.join(', ')}`}</span>,
    [notUploadedAttachments]
  )

  const renderHelpDescription = useMemo(
    () => (
      <Translation text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.other.mandatoryAttachmentsHelp} />
    ),
    []
  )

  const handleOnMandatoryAttachmentsConfirm = useCallback(async () => {
    setIsLoading(true)

    if (hasMandatoryAttachments) {
      if (onConfirm) {
        onConfirm()
      }
      setHasMandatoryAttachments(false)
      setIsMandatoryAttachmentsDialogVisible(false)
      setIsLoading(false)
      return
    }

    const result = await uploadAttachments({
      partnerId: actualData.partner.partnerId,
      attachments: mandatoryAttachments,
    })

    if (result && result.length === mandatoryAttachments.length) {
      updateActualData({
        ...actualData,
        partnerAttachments: [...actualData.partnerAttachments, ...result],
      })
      if (onConfirm) {
        onConfirm()
      }
      setHasMandatoryAttachments(false)
      setIsMandatoryAttachmentsDialogVisible(false)
      setIsLoading(false)
      return
    } else {
      setNotUploadedAttachments(() =>
        mandatoryAttachments
          .filter((attachment) => {
            return !result.some((newAttachment) => newAttachment.entryId === attachment.entryId)
          })
          .map((attachment) => attachment.fileName)
      )
      updateMandatoryAttachments(result)
      setIsLoading(false)
      return
    }
  }, [
    actualData,
    hasMandatoryAttachments,
    mandatoryAttachments,
    onConfirm,
    ownerId,
    setHasMandatoryAttachments,
    updateActualData,
    updateMandatoryAttachments,
  ])

  const handleOnMandatoryAttachmentsReject = useCallback(() => {
    if (onReject) {
      onReject()
    }
    setNotUploadedAttachments([])
    setIsMandatoryAttachmentsDialogVisible(false)
  }, [onReject])

  const handleOnHasMandatoryAttachmentsChange = useCallback(() => {
    setNotUploadedAttachments([])
  }, [])

  const mandatoryAttachmentsContent = useMemo(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <LoaderOverlay show={isLoading} />
        <InfoBlock text={renderHelpDescription} type={InfoBlockType.Help} hasIcon hasCloseButton />
        <RenderWhen condition={Boolean(notUploadedAttachments.length)}>
          <InfoBlock
            text={renderNonUploadedAttachmentsMessage}
            type={InfoBlockType.Danger}
            hasIcon
            hasCloseButton
          />
        </RenderWhen>
        <RenderWhen condition={!hasMandatoryAttachments}>
          <AttachFile
            attachments={mandatoryAttachments}
            formKey={PartnerDataFormKeys.MandatoryAttachments}
            acceptedFormats={ACCEPTABLE_FILE_TYPES}
          />
        </RenderWhen>
        <div style={{ paddingTop: '8px' }}>
          <FormCheckBox
            onChange={handleOnHasMandatoryAttachmentsChange}
            field={MandatoryAttachmentsFields.HasMandatoryAttachments}
            formKey={PartnerDataFormKeys.MandatoryAttachments}
            defaultValue={false}
            label={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.other.hasMandatoryAttachments}
          />
        </div>
      </div>
    ),
    [
      handleOnHasMandatoryAttachmentsChange,
      hasMandatoryAttachments,
      isLoading,
      mandatoryAttachments,
      notUploadedAttachments.length,
      renderHelpDescription,
      renderNonUploadedAttachmentsMessage,
    ]
  )

  return {
    hasMandatoryAttachments: hasMandatoryAttachments || mandatoryAttachments?.length > 0,
    handleOnMandatoryAttachmentsConfirm,
    handleOnMandatoryAttachmentsReject,
    mandatoryAttachmentsContent,
    isMandatoryAttachmentsDialogVisible,
    setIsMandatoryAttachmentsDialogVisible,
  }
}
